import { css } from '@linaria/atomic';
import { onlyText } from 'react-children-utilities';
import type { TableInstance } from 'react-table';
import { theme } from '@frontend/theme';
import { MoreIcon } from '../../../icon/icons/more';
import { PrimaryButton } from '../../buttons/primary/primary-button';
import { SecondaryButton } from '../../buttons/secondary/secondary-button';
import { IconButton } from '../../icon-button/icon-button.component';
import { PopoverMenuItem } from '../../popover/menu/popover-menu-item.component';
import { PopoverMenu } from '../../popover/menu/popover-menu.component';
import { usePopoverMenu } from '../../popover/menu/use-popover-menu';
import { TableData } from '../table-data-type';
import {
  TableIconActions,
  TableButtonActions,
  TableActionIcon,
  TableActionButton,
  ManualFilters,
} from '../table-types';
import { FilterColumnsContainer } from './filter-columns.container';
import { FilterContainer } from './filter.container';

const tableActionsContainerStyle = css`
  padding: ${theme.spacing(0, 1)};
  display: flex;
  align-items: center;
  flex-shrink: 1;
  justify-content: flex-end;
`;
const tableActionBtnMarginLgStyle = css`
  margin-left: ${theme.spacing(2)};
`;

const tableActionBtnMarginSmStyle = css`
  margin-left: ${theme.spacing(1)};
`;

type TableActionsProps<T extends TableData> = {
  tableInstance: TableInstance<T>;
  isFilterEnabled?: boolean;
  tableActions?: (TableIconActions<T> | TableButtonActions<T>)[];
  manualFiltersRender?: ManualFilters<T>;
  onColumnsChange?: (changeEvent: { hiddenColumns?: string[]; columnOrder?: string[] }) => void;
};

export const TableActions = <T extends TableData>({
  tableInstance,
  isFilterEnabled,
  tableActions,
  manualFiltersRender,
  onColumnsChange,
}: TableActionsProps<T>) => {
  const { isMobile, isLoading, uniqueFilterColumnsId, hasFilterColumns, tableTrackingIds } = tableInstance;
  const { getTriggerProps, getMenuProps, getItemProps, close } = usePopoverMenu<
    HTMLButtonElement | HTMLAnchorElement,
    false,
    HTMLMenuElement,
    HTMLAnchorElement | HTMLButtonElement
  >({
    interactionOptions: {
      listNavigation: {
        openOnArrowKeyDown: false,
      },
    },
    placement: 'left-start',
  });

  const triggerProps = getTriggerProps();
  const menuProps = getMenuProps();

  const renderActions = () => {
    return tableActions?.map((tableAction: TableIconActions<T> | TableButtonActions<T>, id) => {
      const { onClick, label, Icon, ...rest } = tableAction;

      const itemProps = getItemProps({
        index: id,
        onClick: (e) => {
          e.stopPropagation();
          onClick?.(tableInstance);
          close();
        },
      });

      type TODO = any;
      if (tableAction?.type === 'button') {
        const { onClick, label, size, Icon, ref, ...rest } = tableAction;
        const { hide, variant, disabled, ...dynamicProps } = Object.entries(rest).reduce(
          (acc, [key, value]) => ({
            ...acc,
            ...{ [key]: typeof value !== 'function' ? value : (value as TODO)(tableInstance) },
          }),
          {} as Omit<TableActionButton<T>, 'onClick' | 'label' | 'size' | 'Icon'>
        );

        if (isMobile && !hide && tableActions?.length > 1) {
          return (
            <PopoverMenuItem
              key={onlyText(label as React.ReactNode) + id}
              Icon={Icon}
              disabled={isLoading || disabled}
              // TODO: Fix this assertion
              ref={ref as any}
              {...itemProps}
              {...dynamicProps}
            >
              {label}
            </PopoverMenuItem>
          );
        }

        return !hide ? (
          variant === 'secondary' ? (
            <SecondaryButton
              key={id}
              size={size ?? 'tiny'}
              className={tableActionBtnMarginLgStyle}
              disabled={isLoading || disabled}
              trackingId={tableTrackingIds.dynamicTableActionItem?.(onlyText(label as React.ReactNode))}
              onClick={(e) => {
                e.stopPropagation();
                onClick?.(tableInstance);
              }}
              {...dynamicProps}
            >
              {label}
            </SecondaryButton>
          ) : (
            <PrimaryButton
              key={id}
              size={size ?? 'tiny'}
              className={tableActionBtnMarginLgStyle}
              trackingId={tableTrackingIds.dynamicTableActionItem?.(onlyText(label as React.ReactNode))}
              disabled={isLoading || disabled}
              onClick={(e) => {
                e.stopPropagation();
                onClick?.(tableInstance);
              }}
              {...dynamicProps}
            >
              {label}
            </PrimaryButton>
          )
        ) : null;
      }

      const { hide, type, disabled, ...dynamicProps } = Object.entries(rest).reduce(
        (acc, [key, value]) => ({
          ...acc,
          ...{ [key]: typeof value !== 'function' ? value : value(tableInstance) },
        }),
        {} as Omit<TableActionIcon<T>, 'onClick' | 'Icon' | 'label'>
      );

      if (isMobile && !hide && tableActions?.length > 1) {
        return (
          <PopoverMenuItem
            key={onlyText(label as React.ReactNode) + id}
            Icon={Icon}
            disabled={isLoading || disabled}
            trackingId={tableTrackingIds.dynamicTableActionItem?.(onlyText(label as React.ReactNode))}
            {...itemProps}
            {...(dynamicProps as any)} //TODO: Fix this assertion
          >
            {label}
          </PopoverMenuItem>
        );
      }

      return (
        !hide && (
          <IconButton
            key={id}
            className={tableActionBtnMarginSmStyle}
            color='light'
            disabled={isLoading || disabled}
            onClick={(e) => {
              e.stopPropagation();
              onClick?.(tableInstance);
            }}
            {...dynamicProps}
            label={onlyText(label as React.ReactNode)}
            trackingId={tableTrackingIds.dynamicTableActionItem?.(onlyText(label as React.ReactNode))}
            showLabelOnHover
          >
            {Icon && <Icon />}
          </IconButton>
        )
      );
    });
  };

  const renderActionsSection = () => {
    if (isMobile && tableActions && tableActions?.length > 1) {
      return (
        <>
          <IconButton
            className={tableActionBtnMarginSmStyle}
            color='light'
            label='Action menu'
            disabled={isLoading}
            trackingId={tableTrackingIds.rowActionMenu}
            {...triggerProps}
          >
            <MoreIcon size={20} />
          </IconButton>
          <PopoverMenu {...menuProps} css={{ maxWidth: 320 }}>
            {renderActions()}
          </PopoverMenu>
        </>
      );
    }
    return renderActions();
  };

  const shouldRenderContainer =
    isFilterEnabled || !!tableActions?.length || (!!uniqueFilterColumnsId && hasFilterColumns);

  return shouldRenderContainer ? (
    <div className={tableActionsContainerStyle}>
      {isFilterEnabled && <FilterContainer manualFiltersRender={manualFiltersRender} tableInstance={tableInstance} />}
      {!!uniqueFilterColumnsId && hasFilterColumns && (
        <FilterColumnsContainer
          tableInstance={tableInstance}
          isFilterEnabled={isFilterEnabled}
          onChange={onColumnsChange}
        />
      )}
      {!!tableActions?.length && renderActionsSection()}
    </div>
  ) : null;
};
